import { useEffect } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { getUser } from '~/selectors/user.selectors';
import * as insuranceServices from '~/services/insurance.service';
import { type InsuranceSettings, type InsuranceSettingsJSON } from '~/types/Insurance';
import { newRelic } from '~/utils/newRelic/newRelic';

const INSURANCE_SETTINGS_QUERY_KEY = 'insurance_settings';

const insuranceSettingsSelector = (data: InsuranceSettingsJSON) => ({
  ...data,
  minimum_amount: Number(data.minimum_amount).toFixed(2),
});

export const useInsurance = () => {
  const user = useSelector(getUser);

  const { data, error, isLoading } = useQuery({
    enabled: !!user,
    queryFn: async () => (await insuranceServices.get()).data,
    queryKey: [INSURANCE_SETTINGS_QUERY_KEY],
    refetchOnMount: false,
    select: insuranceSettingsSelector,
  });

  useEffect(() => {
    if (error) {
      newRelic.noticeError('Failed to fetch insurance settings', {
        e: error,
        id: 'InsuranceSettingsMonitor',
      });
    }
  }, [error]);

  return {
    data,
    isLoading,
  };
};

export const useInsuranceUpdate = () => {
  const queryClient = useQueryClient();

  const { isLoading: isUpdating, mutateAsync: updateInsurance } = useMutation<
    InsuranceSettings,
    Error,
    InsuranceSettings
  >({
    mutationFn: async (insuranceSettings) => {
      const { data } = await insuranceServices.put({
        ...insuranceSettings,
        minimum_amount: parseFloat(insuranceSettings.minimum_amount),
      });

      return insuranceSettingsSelector(data);
    },
    onError: (error) => {
      newRelic.noticeError('Failed to update insurance settings', {
        error,
        id: 'InsuranceSettingsMutation',
      });
    },
    onSuccess: (data) => queryClient.setQueryData([INSURANCE_SETTINGS_QUERY_KEY], data),
  });

  return {
    isUpdating,
    updateInsurance,
  };
};
